import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
  IconButton,
  Typography,
  Grid,
  Paper,
  Button,
  Box
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import LoadingButton from '../components/LoadingButton'
import LoadingCircle from '../components/common/LoadingCircle'
import { smallScreenWidth } from '../constants/AppConstants'
import OpenSearchDropdown from '../components/OpenSearchDropdown'
import { AntTabs } from '../components/VendorTabs'
import { debounce } from 'lodash'
import CandidateMain from '../components/CandidateMain'
import CandidateTalentProfile from '../components/CandidateTalentProfile'
import { DashboardAthenaTable } from '../components/DashboardAthenaTable'
import { candidateReferencesCols, candidateRequisitionsCols } from '../constants/CandidateCols'
import fetchFromAthena from '../functions/FetchFromAthena'

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    margin: '2rem 1rem'
  }
}))

const defaultHeaders = {
  hrOrg: '',
  candidate: ''
}


function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`candidate-tabpanel-${index}`}
      aria-labelledby={`candidate-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          {children}
        </Box>
      )}
    </div>
  )
}

export default function Candidate(props) {
  const [state, setState] = useState({
    fetched: true,
    isLoading: false
  })
  const [showTable, setShowTable] = useState(false)
  const [headers, setHeaders] = useState(defaultHeaders)
  const [tabValue, setTabValue] = useState(0)
  const [tableRefreshKey, setTableRefreshKey] = useState(0)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)
  const [candidateData, setCandidateData] = useState({})
  const [isLoadingMainData, setIsLoadingMainData] = useState(false)
  const [hrOrg, setHrOrg] = useState('')
  const [candidate, setCandidate] = useState('')
  const [clearTrigger, setClearTrigger] = useState(false)
  const classes = useStyles()

  const { credentials } = props.fetchInitialData

  const debouncedSubmit = React.useCallback(
    debounce(() => {
      setTableRefreshKey(prevKey => prevKey + 1)

      setTimeout(() => {
        setIsSubmitDisabled(false)
      }, 2000)
    }, 300),
    [headers]
  )

  const handleSubmit = e => {
    e.preventDefault()
    if (!isSubmitDisabled) {
      setIsSubmitDisabled(true)
      setShowTable(false)
      setTabValue(0)
      setState(prevState => ({ ...prevState, isLoading: true }))
      debouncedSubmit()
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const fetchMainData = async () => {
    try {
      setIsLoadingMainData(true)

      const data = await Promise.allSettled(
        ['hr_candidate_main'].map(query =>
          fetchFromAthena({
            queryName: query,
            headers,
            accessToken: credentials.user.accessToken,
            credentials
          })
        )
      ).then(results => results)

      setCandidateData(data?.[0]?.value?.[0] || {})
      setShowTable(true)
      setIsLoadingMainData(false)
      setState(prevState => ({ ...prevState, isLoading: false }))
    } catch (error) {
      console.error('Error fetching candidate main data:', error)
      setCandidateData({})
      setIsLoadingMainData(false)
      setState(prevState => ({ ...prevState, isLoading: false }))
    }
  }

  useEffect(() => {
    if (tableRefreshKey > 0) {
      fetchMainData()
    }
  }, [tableRefreshKey])

  if (!state.fetched) {
    return <LoadingCircle />
  }

  return (
    <div style={{ padding: '1.5rem 0.5rem' }}>
      <IconButton
        style={{ borderRadius: '5px', position: 'absolute' }}
        size='small'
        onClick={() => props.history.push('/')}
        aria-label='back'
      >
        <ArrowBackIcon />
        {props.fetchInitialData.credentials.appWidth > smallScreenWidth && 'Dashboard'}
      </IconButton>

      <Typography variant='h5' style={{ textAlign: 'center' }}>
        Candidate
      </Typography>

      <div style={{ margin: '1rem' }}>
        <Paper style={{ padding: '1rem' }}>
          <form
            onSubmit={e => {
              e.preventDefault()
              handleSubmit(e)
            }}
          >
            <Grid
              container
              spacing={2}
              direction='row'
              justifyContent='flex-start'
              alignItems='center'
            >
              <Grid item>
                <OpenSearchDropdown
                  id='hrOrg'
                  required
                  width='250px'
                  label='HR Organization'
                  page='RESOURCE'
                  entity='HRORG'
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
                  onChange={val => {
                    const hrOrgValue = val?.VALUE || null
                    setHrOrg(hrOrgValue)
                    setHeaders({ ...headers, hrOrg: hrOrgValue })
                  }}
                />
              </Grid>
              <Grid item>
                <OpenSearchDropdown
                  id='candidate'
                  required
                  disabled={!hrOrg}
                  width='300px'
                  label='Candidate'
                  page='CANDIDATE'
                  entity='CANDIDATE'
                  searchFields={['DISPLAY']}
                  accessToken={credentials.user.accessToken}
                  credentials={credentials}
                  clearTrigger={clearTrigger}
onChange={val => {
                    const candidateValue = val?.VALUE || null
                    setCandidate(candidateValue)
                    setHeaders({ ...headers, candidate: candidateValue })
                  }}
                />
              </Grid>
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <LoadingButton
                label='Submit'
                isLoading={state.isLoading}
                color='primaryVLButton'
                buttonType='submit'
                disabled={isSubmitDisabled}
              />
              {!!(hrOrg || candidate) && (
                <Button
                  onClick={() => {
                    setHeaders(defaultHeaders)
                    setHrOrg('')
                    setCandidate('')
                    setClearTrigger(prev => !prev)
                  }}
                  style={{ marginLeft: '0.5rem' }}
                >
                  Clear
                </Button>
              )}
            </div>
          </form>
        </Paper>

        {showTable && (
          <div style={{ marginTop: '1rem' }}>
            <Paper>
              <div style={{ padding: '1rem 1rem 0 1rem' }}>
                <Typography variant='h5'>{candidateData.PRESENTATIONNAMESNAPSHOT}</Typography>
                <Typography variant='subtitle2'>Candidate ID: {candidateData.CANDIDATE}</Typography>
              </div>
              <div style={{ padding: '0 1rem' }}>
                <AntTabs
                  value={tabValue}
                  handleChange={handleTabChange}
                  color='#1976d2'
                  tabs={[
                    'Main',
                    'Talent Profile',
                    'References',
                    'Requisitions'
                  ]}
                />
              </div>

              <TabPanel value={tabValue} index={0}>
                <CandidateMain data={candidateData} isLoading={isLoadingMainData} />
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <CandidateTalentProfile
                  {...props}
                  headers={headers}
                  tableRefreshKey={tableRefreshKey}
                />
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`references-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={candidateReferencesCols}
                  reportName='hr_candidate_references'
                />
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <DashboardAthenaTable
                  {...props}
                  isTempTable={false}
                  key={`requisitions-${tableRefreshKey}`}
                  params={{ ...headers }}
                  tableColumns={candidateRequisitionsCols}
                  reportName='hr_candidate_requisitions'
                />
              </TabPanel>

            </Paper>
          </div>
        )}
      </div>
    </div>
  )
}
